import { Helmet } from "react-helmet";
import { SideBar } from "../side-bar/SideBar";
import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpFromBracket, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { api, getToken } from "../../../services/apis/auth-api/apiUrl";
import { EXTRACT_TEXT_FROM_CV, GET_CDD_PROFILE, OCR_PROCESS_END_POINT, PROCESS_CDD_PROFILE } from "../../../config/config";
import { useAuth } from "../../../services/auth-services/authContext";
import { AuthContextState } from "../../../utils/types";

interface CandidateInfo {
  candidate_fullname: string;
  gender: string;
  date_of_birth: string;
  email?: string | null;
  contact_num?: string | null;
  residence?: string | null;
  educational_attainment?: string;
  course?: string;
  employment_history?: string | null;
  cdd_profile: string;
  uploaded_by: number;
  cv_name: string;
}

const CandidateProfile = () => {
    const [file, setFile] = useState<File | null>(null);
    const fileInput = useRef<HTMLInputElement | null>(null);
    const [rawCvText, setRawCvText] = useState<string>('');

    const {decodedJwtData} = useAuth() as AuthContextState;
    const account_id = decodedJwtData?.userId;

    const [formatStat, setFormatStat] = useState<string>('');
    const [uploadLoading, setUploadLoading] = useState<boolean>(false);
    const [uploadFailed, setUploadFailed] = useState<boolean | null>(null);

    const [cddInfos, setCddInfos] = useState<CandidateInfo[] | null>(null);
    
    const handleSetFile = (e : React.ChangeEvent<HTMLInputElement>) => {
        setFile(null);
        if (e.target.files && e.target.files.length > 0) {
            setFile(e.target.files[0]);
        }
    }

    const handleOcrProcess = async() => {
        try{
            console.log(file);
            if(!file) return false;
            const formData = new FormData();
            console.log('Test')
            formData.append('file', file);
            const text = await api.post(OCR_PROCESS_END_POINT, formData, {
                headers : {
                    'Authorization' : 'Bearer '+getToken()
                }
            })
            if(text.data.length === 0){
                setFormatStat('No extractable text detected in the document.');
                setUploadFailed(true);
                if(fileInput.current) fileInput.current.value = '';
                setRawCvText('');
            }else{
                setRawCvText(text.data);
            }
        }catch(e){
            setFormatStat('OCR process failed. Please try again.');
            setUploadFailed(true);
            if(fileInput.current) fileInput.current.value = '';
            setRawCvText('');
        }
    }

    useEffect(()=>{
        const upload = async() => { 
            if(!file) return false;
            const formData = new FormData();
            formData.append('file', file);

            try{
                const text = await api.post(EXTRACT_TEXT_FROM_CV, formData, {
                    headers : {
                        'Authorization' : 'Bearer '+getToken()
                    }
                })
                if(text.data.length <= 100){
                    setFormatStat('Extracted text is smaller than expected. Running OCR extraction for a more complete result.');
                    setTimeout(handleOcrProcess, 3000);
                }else{
                    setRawCvText(text.data);
                }
            }catch(e){
                setUploadLoading(false);
                setUploadFailed(true);
                setFormatStat('Failed to extract text from CV. Please try again later.');
                setFile(null);
                if(fileInput.current) fileInput.current.value = '';
            }
        }
        if(file){
            setUploadLoading(true);
            setFormatStat('Extracting text from CV.....');
            setUploadFailed(null);
            setTimeout(()=>upload(), 3000);
        } 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[file])

    useEffect(()=>{
        const uploadCV = async() => { 
            if(!file) return false;
            const formData = new FormData();
            formData.append('file', file);
            formData.append('cv_text', rawCvText);
            formData.append('id', String(account_id));
            try{
                await api.post(PROCESS_CDD_PROFILE, formData, {
                    headers : {
                        'Authorization' : 'Bearer '+getToken()
                    }
                })
                setUploadFailed(false);
                setFormatStat('CV was processed and uploaded in the database.')
                setTimeout(()=>{
                    setUploadLoading(false);
                    setFormatStat('');
                    setUploadFailed(null);
                    if(fileInput.current) fileInput.current.value = '';
                    setRawCvText('');
                },2000);
            }catch(e){
                setUploadFailed(true);
                setUploadLoading(false);
                setFile(null);
                setFormatStat("Something went wrong — the AI didn't respond.")
                if(fileInput.current) fileInput.current.value = '';
            }
        }
        
        if(rawCvText !== '') {
            setFormatStat("AI started candidate's data analysis.....");
            setTimeout(()=>uploadCV(), 3000);
        }else{
            setUploadLoading(false);
            setUploadFailed(true);
            setFile(null);
            if(fileInput.current) fileInput.current.value = '';
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[rawCvText])

    useEffect(()=>{
        const getCddInfo = async() => {
            try{
                const cdd_res = await api.get(GET_CDD_PROFILE, {
                    headers : {
                        'Authorization' : 'Bearer '+getToken()
                    }
                });
                const cdd_info = cdd_res.data as CandidateInfo[];
                setCddInfos(cdd_info);
            }catch(e){}
        }
        if(uploadFailed === null){
            getCddInfo();
        }

    },[uploadFailed]);

    return(
        <div className="content flex">
            <Helmet>
                <title>CDD Pipeline</title>
            </Helmet>

            <SideBar />

            <div className='main-content'>
                <div>
                    <h2>Candidate's profile</h2>
                </div>
                <div className="bg-white p-4 shadow flex flex-col gap-2 my-4">
                    <div className="flex items-center">
                        <div className="ms-auto flex justify-center items-center gap-4">
                            <div>
                                {formatStat !== '' && (
                                    <small className={uploadLoading && uploadFailed === null ? "text-gray-700" : uploadFailed ? "text-red-400" : "text-green-500"}>
                                        {formatStat}
                                    </small>
                                )}
                            </div>
                            <input type="file" 
                                onChange={(e)=>handleSetFile(e)} 
                                accept=".pdf,.docx,image/png, image/jpeg, image/jpg, image/webp, image/gif, image/bmp, image/tiff, image/svg+xml" 
                                ref={fileInput} 
                                className="hidden" 
                                name="" id="" />

                            {uploadLoading ? (
                                <button type="button"
                                        className="button bg-green-400 cursor-not-allowed text-sm w-fit"
                                        >
                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> &nbsp;
                                        Processing...
                                </button>
                            ): (
                                <button type="button" onClick={() => fileInput.current?.click()} 
                                        className="button button-vcc-green text-sm w-fit"
                                        >
                                        <FontAwesomeIcon icon={faArrowUpFromBracket} /> &nbsp;
                                        Upload CDD CV
                                </button>
                            )}
                            
                        </div>
                    </div>
                    <div className="table-container overflow-x-auto">
                        <table className="w-full h-full border">
                            <thead className="table-fixed-header border">
                                <tr className="bg-gray-100 text-sm">
                                    <th className='table-headers font-medium'>Candidate's Fullname</th>
                                    <th className='table-headers font-medium'>Email</th>
                                    <th className='table-headers font-medium'>Contact Number</th>
                                    <th className='table-headers font-medium'>Candidate's Profile</th>
                                    <th className='table-headers font-medium'>Course</th>
                                    <th className='table-headers font-medium'>Educational Attainment</th>
                                    <th className='table-headers font-medium'>Actions</th>
                                </tr>
                            </thead>
                            <tbody className="text-center">
                                {cddInfos && cddInfos.length > 0 ? cddInfos.map(data => (
                                    <tr className="hover:bg-gray-200">
                                        <td className='table-body'>{data.candidate_fullname}</td>
                                        <td className='table-body'>{data.email}</td>
                                        <td className='table-body'>{data.contact_num}</td>
                                        <td className='table-body'>{data.cdd_profile}</td>
                                        <td className='table-body'>{data.course}</td>
                                        <td className='table-body'>{data.educational_attainment}</td>
                                        <td className='table-body'><div className="text-yellow-600">under developent</div></td>
                                    </tr>
                                )): cddInfos && cddInfos.length <= 0 ? (
                                    <tr>
                                        <td className='table-body px-2' colSpan={11}>No candidate found.</td>
                                    </tr>
                                ) : Array.from({length : 3}).map((_, index) => (
                                    <tr key={index}>
                                        {[...Array(7)].map((_, colIndex) => (
                                            <td key={colIndex} className='table-body'>
                                                <div className="table-loading bg-gray-200"></div>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CandidateProfile;